import React, { useEffect, useRef, useState } from 'react';
import { Animated, Easing, StyleSheet } from 'react-native';

import { runAfterInteractions } from '@/utils/runAfterInteractions';

const defaultColors: string[] = ['#4dabf7', '#3bc9db', '#38d9a9', '#69db7c'];

interface LoadingDotsProps {
    dots?: number;
    colors?: string[];
    size?: number;
    bounceHeight?: number;
    borderRadius?: number;
    components?: React.ReactNode[] | null;
}

/**
 * React Component that creates a smooth loading animation with dots
 * or custom components
 */
export function LoadingDots({
    dots = 4,
    colors = defaultColors,
    size = 20,
    bounceHeight = 20,
    borderRadius,
    components = null,
}: LoadingDotsProps): React.JSX.Element {
    const [animations, setAnimations] = useState<Animated.Value[]>([]);
    const [reverse, setReverse] = useState<boolean>(false);

    const opacity = useRef<Animated.Value>(new Animated.Value(0)).current;

    useEffect(() => {
        const dotAnimations: Animated.Value[] = [];
        const animationsAmount: number = !!components && Array.isArray(components) ? components.length : dots;
        for (let i = 0; i < animationsAmount; i++) {
            dotAnimations.push(new Animated.Value(0));
        }
        setAnimations(dotAnimations);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (animations.length === 0) {
            return;
        }
        loadingAnimation(animations, reverse);
        appearAnimation();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [animations]);

    function appearAnimation(): void {
        Animated.timing(opacity, {
            toValue: 1,
            easing: Easing.ease,
            useNativeDriver: true,
        }).start();
    }

    function floatAnimation(node: Animated.Value, reverseY: boolean, delay: number): Animated.CompositeAnimation {
        const floatSequence = Animated.sequence([
            Animated.timing(node, {
                toValue: reverseY ? bounceHeight : -bounceHeight,
                easing: Easing.bezier(0.41, -0.15, 0.56, 1.21),
                delay,
                useNativeDriver: true,
            }),
            Animated.timing(node, {
                toValue: reverseY ? -bounceHeight : bounceHeight,
                easing: Easing.bezier(0.41, -0.15, 0.56, 1.21),
                delay,
                useNativeDriver: true,
            }),
            Animated.timing(node, {
                toValue: 0,
                delay,
                useNativeDriver: true,
            }),
        ]);
        return floatSequence;
    }

    function loadingAnimation(nodes: Animated.Value[], reverseY: boolean): void {
        Animated.parallel(nodes.map((node, index) => floatAnimation(node, reverseY, index * 100))).start(() => {
            runAfterInteractions(() => {
                setReverse(!reverse);
            });
        });
    }

    useEffect(() => {
        if (animations.length === 0) {
            return;
        }
        loadingAnimation(animations, reverse);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [reverse, animations]);

    return (
        <Animated.View style={[styles.loading, { opacity }]}>
            {animations.map((animation, index) =>
                components ? (
                    <Animated.View key={`loading-anim-${index}`} style={[{ transform: [{ translateY: animation }] }]}>
                        {components[index]}
                    </Animated.View>
                ) : (
                    <Animated.View
                        key={`loading-anim-${index}`}
                        style={[
                            {
                                width: size,
                                height: size,
                                borderRadius: borderRadius || size / 2,
                            },
                            { backgroundColor: colors[index] || '#4dabf7' },
                            { transform: [{ translateY: animation }] },
                        ]}
                    />
                )
            )}
        </Animated.View>
    );
}

const styles = StyleSheet.create({
    loading: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
    },
});
